/**
 * CenterInfo：中宫 2×2 信息区（4 组 justify-between 自适应填充整宫高度）
 * 组1 标题 / 组2 姓名·三行时间·命身主 / 组3 节气+非节气四柱+自化图示 / 组4 六层四化色块
 */
import type { Meta } from '../types/base';
import type { BirthInput } from '../types/api';
import { SELF_ARROW_CLASS, KIND_CHAR } from './star-annot';
import { LAYER_META } from './layer-meta';

interface Props {
  meta: Meta;
  /** 命例名称（匿名显示：首字保留、其余逐字替×） */
  name?: string;
  /** 命例录入原始输入（钟表时间显示，已入库数据透传） */
  input?: BirthInput;
  /** 隐藏生辰：姓名显匿名、阳历/农历/真太阳时与四柱 invisible 占位空白（保留布局不跳动） */
  hideBirth?: boolean;
}

/** 五行着色：木→绿 火→红 土→土色 金→金色 水→蓝 */
const GAN_WX: Record<string, string> = {
  甲: '木', 乙: '木', 丙: '火', 丁: '火', 戊: '土',
  己: '土', 庚: '金', 辛: '金', 壬: '水', 癸: '水',
};
const ZHI_WX: Record<string, string> = {
  子: '水', 丑: '土', 寅: '木', 卯: '木', 辰: '土', 巳: '火',
  午: '火', 未: '土', 申: '金', 酉: '金', 戌: '土', 亥: '水',
};
const WX_CLASS: Record<string, string> = {
  木: 'text-green-600', 火: 'text-red-600', 土: 'text-amber-800',
  金: 'text-yellow-500', 水: 'text-blue-600',
};

const YANG_GAN = new Set(['甲', '丙', '戊', '庚', '壬']);

/** 六层四化对照（本命/大限/流年/流月/流日/流时）：底色与盘面各层四化徽标色一致；小限无四化不占色块 */
const LAYER_BADGES = [
  { label: '本', cls: 'bg-red-600' },
  ...LAYER_META.filter((m) => m.key !== 'xiaoxian').map((m) => ({ label: m.label.slice(1), cls: m.badgeClass })),
];

const pad = (n: number) => String(n).padStart(2, '0');

/** 匿名姓名：首字保留、其余逐字替×；空名→匿名 */
function anonymize(name?: string): string {
  const n = (name ?? '').trim();
  if (!n) return '匿名';
  return n.length <= 1 ? n : n[0] + '×'.repeat(n.length - 1);
}

/** 阳男/阴女/阴男/阳女：年干阴阳 × 性别 */
function yinyangLabel(meta: Meta): string {
  const yang = YANG_GAN.has(meta.sizhu[0][0]);
  return (yang ? '阳' : '阴') + (meta.gender === '男' ? '男' : '女');
}

/** 阳历时间：年月日取引擎换算的阳历日期；时分优先取用户钟表原值
 * （新盘 meta.solar 时分已被真太阳时校正含夏令时回拨覆盖，非钟表时间；旧盘无 input 时回退） */
function solarLabel(meta: Meta, input?: BirthInput): string {
  const [y, m, d] = meta.solar;
  const h = input?.hour ?? meta.solar[3];
  const mi = input?.minute ?? meta.solar[4];
  return `${y}-${pad(m)}-${pad(d)} ${pad(h)}:${pad(mi)}`;
}

/** 农历时间：农历年/月/日 + 时辰（地支+时，如"子时"） */
function lunarLabel(meta: Meta): string {
  const [ly, lm, ld, lhour] = meta.lunar;
  return `${ly}年${lm}月${ld}日 ${lhour}时`;
}

/** 单组竖排四柱（干上支下、逐字五行着色）；sizhu 缺失时显示 4 组占位符（防引擎未返回导致崩溃） */
function SizhuPillars({ sizhu }: { sizhu?: [string, string, string, string] }) {
  const arr = sizhu ?? (['--', '--', '--', '--'] as [string, string, string, string]);
  return (
    <div className="flex gap-1.5">
      {arr.map((gz, i) => (
        <div key={i} className="flex flex-col items-center text-[11px] font-semibold leading-tight sm:text-[12px]">
          <span className={WX_CLASS[GAN_WX[gz[0]]]}>{gz[0]}</span>
          <span className={WX_CLASS[ZHI_WX[gz[1]]]}>{gz[1]}</span>
        </div>
      ))}
    </div>
  );
}

/** 节气/非节气四柱双列块：第一行两标签、第二行两组竖柱 */
function SizhuPair({ jieqi, feijieqi }: { jieqi?: [string, string, string, string]; feijieqi?: [string, string, string, string] }) {
  return (
    <div className="flex items-start gap-4">
      <div className="flex flex-col items-center gap-0.5">
        <span className="whitespace-nowrap text-[9px] text-slate-400">节气四柱</span>
        <SizhuPillars sizhu={jieqi} />
      </div>
      <div className="flex flex-col items-center gap-0.5">
        <span className="whitespace-nowrap text-[9px] text-slate-400">非节气四柱</span>
        <SizhuPillars sizhu={feijieqi} />
      </div>
    </div>
  );
}

export default function CenterInfo({ meta, name, input, hideBirth }: Props) {
  const ts = meta.trueSolar;
  const tsText =
    ts?.solar
      ? `${ts.solar[0]}-${pad(ts.solar[1])}-${pad(ts.solar[2])} ${pad(ts.solar[3])}:${pad(ts.solar[4])}`
      : '--'; // 引擎未返回真太阳时时兜底，避免崩溃
  /** 中宫分组 justify-evenly：组间间距与上下端间距均分（标题不悬顶、按钮不贴底，矮视口自适应收窄不溢出）；
   *  字号按半角字符宽度核算：最长行农历时间 10.5px ≈161px ≤ 小屏中宫内容宽 ~170px（文案保留全称）；
   *  内容最小需求压到 ≤210px（行高 leading-tight/none + 紧 gap）：中宫高=外圈行高×2，
   *  矮视口下行高钉在 108 下限→中宫仅 217px，超出部分会从底部溢出压过底线叠到三行宫格；
   *  overflow-hidden 保险：极端情况宁可裁边也不允许越宫 */
  return (
    <div className="flex h-full flex-col items-center justify-evenly overflow-hidden bg-slate-50 p-1.5 max-sm:p-1">
      {/* 组1 标题 */}
      <span className="text-sm font-bold leading-none tracking-wide">元紫@东派紫微斗数</span>

      {/* 组2 基本信息（姓名/三行时间/命身主）：leading-tight 压行盒；窄屏 10.5px 防农历行（最长行）折行 */}
      <div className="flex flex-col items-center gap-0.5 leading-tight">
        {/* 字段间不间断空格分隔（HTML 折叠普通空格）：与命主/身主行同款写法 */}
        <span className="text-[10.5px] sm:text-[12px]">
          姓名：{hideBirth ? '匿名' : anonymize(name)}{'\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0'}{yinyangLabel(meta)}{'\u00A0\u00A0'}{meta.ju}
        </span>
        {/* 时间三行：隐藏时 invisible 占位（区域空白、布局不跳） */}
        <span className={`text-[10.5px] text-slate-600 sm:text-[12px] ${hideBirth ? 'invisible' : ''}`}>阳历时间：{solarLabel(meta, input)}</span>
        <span className={`text-[10.5px] text-slate-600 sm:text-[12px] ${hideBirth ? 'invisible' : ''}`}>农历时间：{lunarLabel(meta)}</span>
        <span className={`text-[10.5px] text-slate-600 sm:text-[12px] ${hideBirth ? 'invisible' : ''}`}>真太阳时：{tsText}</span>
        <span className="text-[10.5px] sm:text-[12px]">
          命主：{meta.mingZhu}{'\u00A0\u00A0'}身主：{meta.shenZhu}
        </span>
      </div>

      {/* 组3 四柱（节气/非节气双列）+ 自化图示（图示行守 10px：11px 在 360px 小屏贴宽上限） */}
      <div className="flex flex-col items-center gap-1 leading-tight">
        {/* 四柱块：隐藏时整块 invisible 占位（含标签，区域空白） */}
        <div className={hideBirth ? 'invisible' : ''}>
          <SizhuPair jieqi={meta.sizhuJieqi} feijieqi={meta.sizhu} />
        </div>
        <div className="flex items-center gap-1 rounded border border-red-200 bg-white px-1 py-0.5 text-[10px] sm:text-[11px]">
          <span className="whitespace-nowrap text-slate-500">自化图示：</span>
          {(Object.keys(SELF_ARROW_CLASS) as (keyof typeof SELF_ARROW_CLASS)[]).map((k) => (
            <span key={k} className={`whitespace-nowrap font-bold ${SELF_ARROW_CLASS[k]}`}>
              {KIND_CHAR[k]}→
            </span>
          ))}
        </div>
      </div>

      {/* 组4 六层四化色块（本限年月日时）：w-4/5 拉满宽度改善点击热区；py-0.5 守中宫高度预算 */}
      <div className="flex w-4/5 gap-1">
        {LAYER_BADGES.map((b) => (
          <span
            key={b.label}
            className={`flex-1 rounded-sm py-0.5 text-center text-[11px] font-bold leading-tight text-white ${b.cls}`}
          >
            {b.label}
          </span>
        ))}
      </div>
    </div>
  );
}
